import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Pin,
  Tag,
  Search,
  Download,
  Upload,
  Filter,
  StickyNote,
  Calendar,
  TrendingUp
} from 'lucide-react';
import { notesTracker } from '../lib/notesTracker';
import type { FrameworkNote } from '../lib/notesTracker';

export const MyNotes: React.FC = () => {
  const [notes, setNotes] = useState<FrameworkNote[]>([]);
  const [filteredNotes, setFilteredNotes] = useState<FrameworkNote[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);
  const [allTags, setAllTags] = useState<string[]>([]);

  useEffect(() => {
    loadNotes();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [notes, searchQuery, selectedTag, showPinnedOnly]);

  const loadNotes = () => {
    const allNotes = notesTracker.getAllNotes();
    const tags = notesTracker.getAllTags();
    setNotes(allNotes);
    setAllTags(tags);
  };

  const applyFilters = () => {
    let result = [...notes];

    // Filter by pinned
    if (showPinnedOnly) {
      result = result.filter(n => n.isPinned);
    }

    // Filter by tag
    if (selectedTag) {
      result = result.filter(n => n.tags.includes(selectedTag));
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(n =>
        n.content.toLowerCase().includes(query) ||
        n.frameworkName.toLowerCase().includes(query) ||
        n.tags.some(t => t.toLowerCase().includes(query))
      );
    }

    setFilteredNotes(result);
  };

  const handleExportNotes = () => {
    const json = notesTracker.exportNotes();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `peak-priorities-notes-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportNotes = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      const success = notesTracker.importNotes(content);
      if (success) {
        alert('Notes imported successfully!');
        loadNotes();
      } else {
        alert('Failed to import notes. Please check the file format.');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  const stats = notesTracker.getStats();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-charcoal-900 mb-2 flex items-center gap-3">
          <StickyNote className="h-10 w-10 text-consulting-slate-blue-600" />
          My Framework Notes
        </h1>
        <p className="text-charcoal-600">
          Your personal annotations, insights, and reflections on frameworks
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="h-5 w-5 text-consulting-slate-blue-600" />
            <span className="text-sm text-charcoal-600">Total Notes</span>
          </div>
          <div className="text-3xl font-bold text-charcoal-900">{stats.totalNotes}</div>
        </div>

        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-2 mb-2">
            <Pin className="h-5 w-5 text-[var(--mustard)]" />
            <span className="text-sm text-charcoal-600">Pinned</span>
          </div>
          <div className="text-3xl font-bold text-charcoal-900">{stats.pinnedNotes}</div>
        </div>

        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="h-5 w-5 text-sage-600" />
            <span className="text-sm text-charcoal-600">Tags</span>
          </div>
          <div className="text-3xl font-bold text-charcoal-900">{stats.totalTags}</div>
        </div>

        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-coral-600" />
            <span className="text-sm text-charcoal-600">Recent (7d)</span>
          </div>
          <div className="text-3xl font-bold text-charcoal-900">{stats.recentNotes}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search notes by content, framework name, or tags..."
              className="w-full pl-10 pr-4 py-2 border-2 border-[var(--rule)] focus:border-consulting-slate-blue-500 focus:outline-none"
            />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPinnedOnly(!showPinnedOnly)}
              className={`px-4 py-2  border-2 transition-colors flex items-center gap-2 ${
                showPinnedOnly
                  ? 'border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)]'
                  : 'border-[var(--rule)] text-charcoal-700 hover:bg-[var(--surface)]'
              }`}
            >
              <Pin className="h-4 w-4" />
              Pinned Only
            </button>

            <button
              onClick={handleExportNotes}
              className="px-4 py-2 border-2 border-[var(--rule)] text-charcoal-700 hover:bg-[var(--surface)] transition-colors flex items-center gap-2"
              title="Export all notes as JSON"
            >
              <Download className="h-4 w-4" />
              Export
            </button>

            <label className="px-4 py-2 border-2 border-[var(--rule)] text-charcoal-700 hover:bg-[var(--surface)] transition-colors flex items-center gap-2 cursor-pointer">
              <Upload className="h-4 w-4" />
              Import
              <input
                type="file"
                accept=".json"
                onChange={handleImportNotes}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Tag Filter */}
        {allTags.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[var(--rule)]">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="h-4 w-4 text-charcoal-600" />
              <span className="text-sm font-semibold text-charcoal-700">Filter by tag:</span>
              {selectedTag && (
                <button
                  onClick={() => setSelectedTag(null)}
                  className="text-xs text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 font-medium"
                >
                  Clear filter
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedTag === tag
                      ? 'bg-sage-600 text-white'
                      : 'bg-sage-100 text-sage-800 hover:bg-sage-200'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Notes List */}
      {filteredNotes.length > 0 ? (
        <div className="space-y-4">
          {filteredNotes.map(note => (
            <div
              key={note.id}
              className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6 hover:border-consulting-slate-blue-300 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Link
                      to={`/elevation/entry/${note.frameworkId}`}
                      className="text-xl font-bold text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 transition-colors"
                    >
                      {note.frameworkName}
                    </Link>
                    {note.isPinned && (
                      <Pin className="h-4 w-4 text-[var(--mustard)] fill-amber-600" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-charcoal-500">
                    <Calendar className="h-3 w-3" />
                    <span>
                      Updated {new Date(note.updatedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-charcoal-800 mb-4 whitespace-pre-wrap line-clamp-3">
                {note.content}
              </p>

              {note.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {note.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-sage-100 text-sage-800 rounded-full text-xs"
                    >
                      <Tag className="h-3 w-3" />
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <Link
                to={`/elevation/entry/${note.frameworkId}#my-notes`}
                className="inline-flex items-center gap-1 text-sm text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 font-medium"
              >
                View Framework & Edit Note →
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-[var(--surface)] border-2 border-dashed border-[var(--rule)]">
          <StickyNote className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-charcoal-900 mb-2">
            {notes.length === 0 ? 'No Notes Yet' : 'No Matching Notes'}
          </h3>
          <p className="text-charcoal-600 mb-6">
            {notes.length === 0
              ? 'Start adding notes to frameworks as you explore them'
              : 'Try adjusting your search or filters'}
          </p>
          {notes.length === 0 && (
            <Link
              to="/elevation/models"
              className="inline-flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700 text-white transition-colors"
            >
              <BookOpen className="h-4 w-4" />
              Explore Frameworks
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

